import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Button, Space, Upload, Image, message, Row, Col, Progress, Checkbox, Modal, Collapse, Tag, Spin } from 'antd';
import { UploadOutlined, LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import type { UploadFile, UploadChangeParam } from 'antd/es/upload/interface';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { ImageObject } from '../../types/vehicle';
import { useSwipeable } from 'react-swipeable';
import { authPpiApiAxiosWithRefresh } from '../../services/api';
import { CAR_PART_CATEGORIES, carPartCategoryMap, carPartImageMap, carPartCategoryDescriptions } from '../../services/imageCategories';

const { Title, Text, Paragraph } = Typography;

interface ImageReviewProps {
  initialImages: ImageObject[];
  onSubmit: (images: ImageObject[], skipAnalysis: boolean) => void;
  onBack: () => void;
  docId?: string;
  onUploadSuccess: () => void;
  isManual: boolean;
}

export const ImageReview: React.FC<ImageReviewProps> = ({
  initialImages,
  onSubmit,
  onBack,
  docId,
  onUploadSuccess,
  isManual
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [thumbnailOffset, setThumbnailOffset] = useState(0);
  const [skipImageAnalysis, setSkipImageAnalysis] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [images, setImages] = useState<ImageObject[]>(initialImages);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [imageToDeleteUrl, setImageToDeleteUrl] = useState<string | null>(null);
  const [dontShowDeleteModalAgain, setDontShowDeleteModalAgain] = useState(false);
  const [uploadingCategory, setUploadingCategory] = useState<string | null>(null);
  const [isCategorizing, setIsCategorizing] = useState(false);

  const handleCategorizedUpload = async (file: File, category: string) => {
    if (!docId) {
      message.error('Document ID is required for upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    setUploadingCategory(category);

    try {
      const response = await (await authPpiApiAxiosWithRefresh()).post(`/upload-categorized-image/${docId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.data.success) {
        message.success(`Uploaded image for ${carPartCategoryMap[category]}`);
        onUploadSuccess(); // Refresh the entire image list
      } else {
        message.error(`Failed to upload image: ${response.data.error || 'Upload failed'}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Network error';
      message.error(`Failed to upload image: ${errorMessage}`);
    } finally {
      setUploadingCategory(null);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setImages(initialImages);
  }, [initialImages]);

  useEffect(() => {
    const dontShow = localStorage.getItem('dontShowDeleteModalAgain') === 'true';
    setDontShowDeleteModalAgain(dontShow);
  }, []);

  const executeDelete = async (imageUrl: string) => {
    if (!docId || deleting) return;
    setDeleting(true);
    try {
      await (await authPpiApiAxiosWithRefresh()).delete(`/delete-image/${docId}`, {
        data: { image_url: imageUrl },
      });
      message.success('Image deleted successfully');
      setImages(prevImages => prevImages.filter(img => img.url !== imageUrl));
    } catch (err) {
      message.error('Failed to delete image. Please try again.');
    } finally {
      setDeleting(false);
      setIsDeleteModalVisible(false);
    }
  };

  const handleDeleteClick = (imageObject: ImageObject) => {
    if (dontShowDeleteModalAgain) {
      executeDelete(imageObject.url);
    } else {
      setImageToDeleteUrl(imageObject.url);
      setIsDeleteModalVisible(true);
    }
  };

  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailTrackRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);



  const updateSkipStatus = async (shouldSkip: boolean) => {
    if (!docId) return;
    setIsUpdating(true);
    try {
      await (await authPpiApiAxiosWithRefresh()).post(`/skip-image-analysis/${docId}`, {
        skip: shouldSkip,
      });
    } catch (err) {
      message.error('Failed to update status. Please try again.');
      // Revert the optimistic state change on failure
      setSkipImageAnalysis(!shouldSkip);
    } finally {
      setIsUpdating(false);
    }
  };

  // Effect to fetch the initial skip status when the component loads
  useEffect(() => {
    const fetchSkipStatus = async () => {
      if (!docId) {
        setIsLoadingStatus(false);
        return;
      }
      try {
        const response = await (await authPpiApiAxiosWithRefresh()).get(`/analysis-status/${docId}`);
        const imageStatus = response.data?.steps?.image_analysis;
        setSkipImageAnalysis(imageStatus === 'skipped');
      } catch (err) {
        console.error("Could not fetch image analysis status", err);
        setSkipImageAnalysis(false);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    fetchSkipStatus();
  }, [docId]);

  // Effect to adjust thumbnail carousel when index changes
  useEffect(() => {
    if (thumbnailContainerRef.current && thumbnailTrackRef.current && thumbnailRefs.current[currentImageIndex]) {
      const container = thumbnailContainerRef.current;
      const track = thumbnailTrackRef.current;
      const activeThumbnail = thumbnailRefs.current[currentImageIndex]!;

      const containerWidth = container.offsetWidth;
      const trackWidth = track.scrollWidth;
      const activeThumbnailWidth = activeThumbnail.offsetWidth;
      const activeThumbnailOffsetLeft = activeThumbnail.offsetLeft;

      // Center the active thumbnail in the container
      let newOffset = (containerWidth / 2) - activeThumbnailOffsetLeft - (activeThumbnailWidth / 2);

      // Clamp the offset to prevent showing empty space on either side
      const maxOffset = 0;
      const minOffset = containerWidth - trackWidth;

      if (trackWidth < containerWidth) {
        newOffset = 0;
      } else {
        newOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
      }

      setThumbnailOffset(newOffset);
    }
  }, [currentImageIndex, images.length]);

  useEffect(() => {
    if (currentImageIndex >= images.length && images.length > 0) {
      setCurrentImageIndex(images.length - 1);
    } else if (images.length === 0) {
      setCurrentImageIndex(0);
    }


  }, [images, images.length, currentImageIndex]);

  const handleFileChange = (info: UploadChangeParam<UploadFile>) => {
    setFileList(info.fileList);
  };

  const handleBatchUpload = async () => {
    const filesToUpload = fileList.filter(file => file.originFileObj);
    const totalFiles = filesToUpload.length;

    if (!docId) {
      message.error('Document ID is required for upload');
      return;
    }

    if (totalFiles === 0) {
      message.warning('No files selected for upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    let successCount = 0;
    const CHUNK_SIZE = 4;
    const chunks = [];

    for (let i = 0; i < totalFiles; i += CHUNK_SIZE) {
      chunks.push(filesToUpload.slice(i, i + CHUNK_SIZE));
    }

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const formData = new FormData();

      chunk.forEach(file => {
        formData.append('files', file.originFileObj as File, file.name);
      });

      try {
        const response = await (await authPpiApiAxiosWithRefresh()).post(`/upload-images/${docId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // Increased timeout for batch
        });

        if (response.data.success) {
          successCount += chunk.length;
          setUploadProgress(Math.round((successCount / totalFiles) * 100));
        } else {
          message.error(`Failed to upload chunk ${i + 1}: ${response.data.error || 'Server error'}. Stopping upload.`);
          setUploading(false);
          return;
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Network error';
        message.error(`Upload failed on chunk ${i + 1}: ${errorMessage}. Stopping upload.`);
        setUploading(false);
        return;
      }
    }

    setUploading(false);
    message.success(`Successfully uploaded ${successCount} of ${totalFiles} images.`);
    setFileList([]); // Clear the list only on full success
    onUploadSuccess(); // Trigger the first fetch, which will start the polling effect
  };

  // Effect to poll for categorization status
  useEffect(() => {
    // Check if there are any images that are still pending categorization
    const uncategorizedImages = images.some(img => !img.category);

    if (uncategorizedImages) {
      setIsCategorizing(true);
      const intervalId = setInterval(() => {
        onUploadSuccess();
      }, 3000); // Poll every 3 seconds

      // Cleanup function to clear the interval
      return () => {
        clearInterval(intervalId);
      };
    } else {
      setIsCategorizing(false);
    }
  }, [images, onUploadSuccess]);

  const handleSubmit = () => {
    if (fileList.length > 0) {
      message.warning('You have selected images that are not uploaded. Please upload them or remove them before continuing.');
      return;
    }
    onSubmit(images, skipImageAnalysis);
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrevious(),
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handleSkipAnalysisChange = (e: CheckboxChangeEvent) => {
    if (isUpdating || isLoadingStatus) return; // Prevent changes while an update is in progress

    const isChecked = e.target.checked;
    setSkipImageAnalysis(isChecked); // Optimistically update the UI

    if (isChecked) {
      // If checking the box, open the modal for confirmation
      setIsModalVisible(true);
    } else {
      // If unchecking, call the API immediately
      updateSkipStatus(false);
    }
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    // The checkbox is already checked from the optimistic update, so just call the API
    updateSkipStatus(true);
  };

  const handleModalCancel = () => {
    // If the user cancels, revert the optimistic UI change
    setSkipImageAnalysis(false);
    setIsModalVisible(false);
  };

  const existingCategories = new Set(images.map(img => img.category).filter(Boolean));
  const missingCategories = CAR_PART_CATEGORIES.filter(
    category => category !== 'unknown' && !existingCategories.has(category)
  );

  return (
    <Card>
      <Title level={4}>{isManual ? 'Add Images' : 'Review & Add Photos to Analyze'}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        {images.length === 0 ? (
          <>
            Upload any images you may have of the vehicle. We explicitly define which images are most useful
            for our analysis. You can always bulk upload them and we will tell you what categories are missing.
          </>
        ) : (
          <>
            Our AI analyzes photos for potential issues like cosmetic damage, rust, and tire wear. Add your own photos or request more from the seller for a more complete picture.
            💡 More images = more accurate insights.
          </>
        )}
      </Text>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {images.length > 0 && images[currentImageIndex] && (
          <Card
            title={
              <Space>
                <span>{`Image ${currentImageIndex + 1} of ${images.length}`}</span>
                {isCategorizing && (
                  <>
                    <Spin size="small" />
                    <Text type="secondary">Classification in progress...</Text>
                  </>
                )}
              </Space>
            }
          >
            {/* Main Image Carousel */}
            <Row justify="center" align="middle" gutter={16} style={{ marginBottom: '24px' }}>
              {!isMobile && (
                <Col flex="none">
                  <Button icon={<LeftOutlined />} onClick={handlePrevious} disabled={images.length <= 1} />
                </Col>
              )}
              <Col flex="auto" style={{ textAlign: 'center', position: 'relative' }} {...(isMobile ? swipeHandlers : {})}>
                {images[currentImageIndex] && images[currentImageIndex].category && (
                  <Tag
                    color="blue"
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 10,
                      textTransform: 'capitalize'
                    }}
                  >
                    {images[currentImageIndex]!.category!.replace(/_/g, ' ')}
                  </Tag>
                )}
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => handleDeleteClick(images[currentImageIndex])}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    border: 'none'
                  }}
                  shape="circle"
                  danger
                  loading={deleting}
                />
                <Image
                  width="100%"
                  style={{ maxHeight: 400, objectFit: 'contain', borderRadius: '8px' }}
                  src={images[currentImageIndex].url}
                  alt={`Vehicle image ${currentImageIndex + 1}`}
                  preview={false}
                />
              </Col>
              {!isMobile && (
                <Col flex="none">
                  <Button icon={<RightOutlined />} onClick={handleNext} disabled={images.length <= 1} />
                </Col>
              )}
            </Row>

            {/* Thumbnails */}
            {images.length > 1 && (
              <Row justify="center">
                <Col style={{ width: '100%', maxWidth: '500px' }}>
                  <div ref={thumbnailContainerRef} style={{ overflow: 'hidden' }}>
                    <div
                      ref={thumbnailTrackRef}
                      style={{
                        display: 'flex',
                        transition: 'transform 0.4s ease-in-out',
                        transform: `translateX(${thumbnailOffset}px)`
                      }}
                    >
                      {images.map((img, index) => (
                        <div
                          key={index}
                          ref={el => thumbnailRefs.current[index] = el}
                          style={{ padding: '0 4px' }}
                        >
                          <Image
                            src={img.url}
                            width={80}
                            height={60}
                            preview={false}
                            onClick={() => handleThumbnailClick(index)}
                            style={{
                              cursor: 'pointer',
                              borderRadius: '4px',
                              border: currentImageIndex === index ? '2px solid #1890ff' : '2px solid #d9d9d9',
                              objectFit: 'cover',
                              imageRendering: 'high-quality' as any
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card>
        )}

        <Card title="Get a Deeper Analysis">
          <Paragraph>
            Elevate your report's accuracy! Our AI performs best with a complete visual record. Consider adding photos for these missing categories. Upload individually or bulk upload below.
          </Paragraph>

          {missingCategories.length > 0 ? (
            <Image.PreviewGroup>
              <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', gap: '16px', padding: '8px 4px', maxHeight: '400px' }}>
                {missingCategories.map(category => (
                  <Card
                    key={category}
                    style={{ border: '1px solid #f0f0f0' }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    {isMobile ? (
                      <div>
                        <div style={{ marginBottom: '8px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
                          <Text strong>{carPartCategoryMap[category] || category}</Text>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
                          <Upload
                            customRequest={({ file }) => handleCategorizedUpload(file as File, category)}
                            showUploadList={false}
                            accept=".jpg,.jpeg,.png"
                          >
                            <Button icon={<UploadOutlined />} loading={uploadingCategory === category} />
                          </Upload>
                          <Image
                            width={80}
                            height={60}
                            src={carPartImageMap[category]}
                            alt={''}
                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                          />
                        </div>
                        <div>
                          <Text type="secondary">{carPartCategoryDescriptions[category]}</Text>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <Upload
                          customRequest={({ file }) => handleCategorizedUpload(file as File, category)}
                          showUploadList={false}
                          accept=".jpg,.jpeg,.png"
                        >
                          <Button icon={<UploadOutlined />} loading={uploadingCategory === category} />
                        </Upload>
                        <Image
                          width={80}
                          height={60}
                          src={carPartImageMap[category]}
                          alt={''}
                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <Text strong>{carPartCategoryMap[category] || category}</Text>
                          <br />
                          <Text type="secondary">{carPartCategoryDescriptions[category]}</Text>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </Image.PreviewGroup>
          ) : (
            <Paragraph type="success" style={{ marginBottom: 16, marginLeft: 16, fontSize: '16px', fontWeight: 500 }}>
              🚀 All Recommended Photos Provided!
            </Paragraph>
          )}

          <Collapse
            style={{
              marginTop: 10,
              marginBottom: 16,
              backgroundColor: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '4px'
            }}
            bordered={false}
          >
            <Collapse.Panel
              header={<Typography.Text style={{ fontSize: '16px' }}>Missing Photos or need more time?</Typography.Text>}
              key="1"
            >
              <Typography.Text>
                You should request these from the seller if missing, or take photos to re-run our analysis after you test drive the vehicle. In the meantime, you can run a report without some photos, and re-run your report with more photos in the future.
              </Typography.Text>
            </Collapse.Panel>
          </Collapse>


          {uploading && (
            <div style={{ marginBottom: 16 }}>
              <Text>Uploading {fileList.length} image{fileList.length > 1 ? 's' : ''}...</Text>              <Progress percent={uploadProgress} status="active" />
            </div>
          )}

          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            multiple
            listType="picture"
            beforeUpload={() => false}
            accept=".jpg,.jpeg,.png"
            disabled={uploading}
          >
            <Button icon={<UploadOutlined />} disabled={uploading}>
              Add Images (Bulk Upload)
            </Button>
          </Upload>

          {fileList.length > 0 && (
            <Button
              type="primary"
              onClick={handleBatchUpload}
              disabled={uploading}
              loading={uploading}
              style={{ marginTop: 16 }}
            >
              Upload {fileList.length} Image{fileList.length > 1 ? 's' : ''}
            </Button>
          )}
        </Card>

        <Row justify="space-between" align="middle">
          <Col style={isMobile ? { marginBottom: '16px' } : {}}>
            <Checkbox checked={skipImageAnalysis} onChange={handleSkipAnalysisChange} disabled={isUpdating || isLoadingStatus}>
              Skip Image Analysis Step
            </Checkbox>
          </Col>
          <Col>
            <Space>
              <Button onClick={onBack}>Back</Button>
              <Button type="primary" onClick={handleSubmit} disabled={uploading || fileList.length > 0}>
                Confirm Images & Continue
              </Button>
            </Space>
          </Col>
        </Row>
      </Space>
      <Modal
        title="Skip Image Analysis"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>By checking this box, we will ignore any found images and not analyze them in the PPI report. Without images, your report may miss visual issues but will be faster to generate. </p>
      </Modal>
      <Modal
        title="Confirm Deletion"
        open={isDeleteModalVisible}
        onOk={() => imageToDeleteUrl && executeDelete(imageToDeleteUrl)}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        confirmLoading={deleting}
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this image? If you change your mind, you will have to re-upload it.</p>
        <Checkbox checked={dontShowDeleteModalAgain} onChange={(e) => {
          const checked = e.target.checked;
          setDontShowDeleteModalAgain(checked);
          localStorage.setItem('dontShowDeleteModalAgain', String(checked));
        }}>
          Don't show this message again
        </Checkbox>
      </Modal>
    </Card>
  );
};